import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  GraduationCap,
  Award,
  Users,
  TrendingUp,
  ArrowRight,
  BookOpen,
  Trophy,
  Sparkles,
  Calendar,
  Bell,
  Phone,
  MessageCircle
} from 'lucide-react';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import Layout from '@/components/Layout';


const HERO_IMAGE = 'images/1_11.jpeg';
const SCHOOL_LOGO = 'https://customer-assets.emergentagent.com/job_89f0a926-232e-4f27-8bd9-23de150e4a0b/artifacts/ydf6w6ly_New%20St.%20Franic%20School%20Logo.jpg';

function AnimatedCounter({ end, duration = 2000, suffix = '' }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      setCount(Math.floor(end * percentage));

      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
}

export default function HomePage() {
  const { t } = useLanguage();
  const [newsIndex, setNewsIndex] = useState(0);

  const newsItems = [
    { title: 'Annual Sports Day - March 15, 2026', type: 'event' },
    { title: '96% Board Result Achievement!', type: 'achievement' },
    { title: 'New Computer Lab Inaugurated', type: 'news' },
    { title: 'PTM Scheduled - March 20, 2026', type: 'event' },
    { title: 'Scholarship Program Applications Open', type: 'news' }
  ];

  const newsLength = newsItems.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setNewsIndex((prev) => (prev + 1) % newsItems.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [newsItems.length]);

  const stats = [
    { icon: Users, value: 1100, suffix: '+', label: t('home.studentsCount'), color: 'text-blue-600' },
    { icon: Award, value: 96, suffix: '%', label: t('home.resultRate'), color: 'text-amber-600' },
    { icon: TrendingUp, value: 24, suffix: '+', label: t('home.yearsExperience'), color: 'text-green-600' },
    { icon: Trophy, value: 24, suffix: '', label: '80%+ Scorers', color: 'text-purple-600' }
  ];

  const highlights = [
    {
      icon: BookOpen,
      title: 'English Speaking Program',
      description: 'Enhancing communication skills and building confidence',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      icon: GraduationCap,
      title: 'Smart Classrooms',
      description: 'Technology-enabled learning environment',
      color: 'bg-amber-100 text-amber-700'
    },
    {
      icon: Trophy,
      title: 'Leadership Program',
      description: 'Nurturing leadership qualities and responsibility',
      color: 'bg-green-100 text-green-700'
    },
    {
      icon: Sparkles,
      title: 'Coding Classes',
      description: 'Basic computer education and programming',
      color: 'bg-purple-100 text-purple-700'
    }
  ];

  const upcomingEvents = [
    { date: 'Mar 15', title: 'Annual Sports Day', type: 'Sports' },
    { date: 'Mar 20', title: 'Parent-Teacher Meeting', type: 'Academic' },
    { date: 'Mar 25', title: 'Science Exhibition', type: 'Cultural' }
  ];

  const programs = [
    "Spoken English",
    "Coding Classes",
    "Smart Classroom",
    "Leadership Program",
    "Sports Training"
  ];

  const testimonials = [
    {
      name: "Rohit Sharma",
      text: "Excellent teaching staff and disciplined environment."
    },
    {
      name: "Priya Verma",
      text: "My child improved confidence and communication."
    },
    {
      name: "Ankit Jain",
      text: "Best school in the area with modern facilities."
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            filter: 'brightness(0.5)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/80 via-blue-900/70 to-blue-950/80" />

        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* School Logo */}
              <div className="flex justify-center mb-8">
                <motion.img
                  src={SCHOOL_LOGO}
                  alt="School Logo"
                  className="h-32 w-32 rounded-full ring-4 ring-amber-500 shadow-2xl animate-float"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                />
              </div>

              <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
                {t('home.welcome')}
              </h1>

              <p className="text-2xl md:text-3xl text-amber-400 font-semibold">
                {t('home.tagline')}
              </p>

              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                {t('home.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Link to="/about">
                  <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg px-8 py-6">
                    {t('home.learnMore')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold text-lg px-8 py-6">
                    {t('home.applyNow')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-white rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* News Ticker */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 py-3 shadow-lg" data-testid="news-ticker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3">
            <Bell className="w-5 h-5 text-white animate-pulse" />
            <span className="text-white font-semibold text-sm">Latest News:</span>
            <div className="flex-1 overflow-hidden">
              <motion.div
                key={newsIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="text-white text-sm font-medium"
              >
                {newsItems[newsIndex].title}
              </motion.div>
            </div>
            <Link to="/news">
              <Button size="sm" variant="ghost" className="text-white hover:bg-amber-600">
                View All
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="text-center p-8 hover:scale-105">
                  <stat.icon className={`w-12 h-12 mx-auto mb-4 ${stat.color}`} />
                  <h3 className="text-4xl font-bold text-blue-900 mb-2">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </h3>
                  <p className="text-slate-600 font-medium">{stat.label}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Principal's Message */}
      <section className="py-24 bg-gradient-to-b from-blue-50 to-white" data-testid="principal-message">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative"
          >

            {/* Glass Card */}
            <div className="bg-white/80 backdrop-blur-lg border border-blue-100 shadow-xl rounded-3xl p-10 md:p-14 relative overflow-hidden">

              {/* Decorative Quote */}
              <div className="absolute top-6 left-6 text-blue-200 text-6xl font-serif">
                "
              </div>

              {/* Heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6 relative z-10">
                Principal's Message
              </h2>

              {/* Accent Line */}
              <div className="w-24 h-1 bg-blue-900 rounded-full mb-8"></div>

              {/* Message */}
              <p className="text-lg text-slate-600 leading-relaxed mb-8 relative z-10">
                Welcome to our school. Our institution is committed to providing
                quality education along with strong moral values, discipline, and
                overall personality development. We believe education is not only
                about academic excellence but also about nurturing creativity,
                confidence, and strong character in every child.
              </p>

              {/* Highlight Values */}
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium">
                  Discipline
                </span>

                <span className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium">
                  Creativity
                </span>

                <span className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium">
                  Leadership
                </span>

                <span className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium">
                  Character Building
                </span>
              </div>

              {/* Principal Info */}
              <div className="flex items-center justify-between flex-wrap gap-4">

                <div>
                  <p className="font-bold text-blue-900 text-xl">
                    Sushma Singh
                  </p>
                  <p className="text-slate-600">
                    Principal | B.Sc, B.Ed
                  </p>
                </div>

                <Link to="/management">
                  <Button className="bg-blue-900 hover:bg-blue-800 transition-all duration-300 shadow-md hover:shadow-lg">
                    Read Full Message
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>

              </div>

            </div>

          </motion.div>

        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-100" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We offer a comprehensive educational experience that prepares students for the future
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full hover:shadow-2xl">
                  <div className={`w-16 h-16 rounded-2xl ${highlight.color} flex items-center justify-center mb-4`}>
                    <highlight.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">
                    {highlight.title}
                  </h3>
                  <p className="text-slate-600">
                    {highlight.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 bg-white" data-testid="upcoming-events">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900">
              Upcoming Events
            </h2>
            <Link to="/events">
              <Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white">
                View All Events
                <Calendar className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="hover:shadow-2xl">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-900 text-white rounded-xl p-4 text-center min-w-[70px]">
                      <p className="text-2xl font-bold">{event.date.split(' ')[1]}</p>
                      <p className="text-xs uppercase">{event.date.split(' ')[0]}</p>
                    </div>
                    <div className="flex-1">
                      <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full mb-2">
                        {event.type}
                      </span>
                      <h3 className="text-lg font-bold text-blue-900">
                        {event.title}
                      </h3>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Branches */}

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">

          <h2 className="text-4xl font-bold text-center text-blue-900 mb-12">
            Our School Branches
          </h2>

          <div className="grid md:grid-cols-2 gap-8">

            {/* Main Branch */}
            <GlassCard className="p-6 hover:scale-105 transition">

              <img
                src="/images/1_11.jpeg"
                alt="New St. Francis School Main Campus"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />

              <h3 className="text-2xl font-bold text-blue-900 mb-2">
                New St. Francis School
              </h3>

              <p className="text-slate-600 mb-4">
                Manwani Colony, Behind Bank of India, Near Shivani Hotel, Pithampur, Dhar (M.P.) 454775
              </p>

              <Link to="/HomePage">
                <Button className="text-red-600">
                  Visit Main Campus
                </Button>
              </Link>

            </GlassCard>


            {/* Pithampur Branch */}
            <GlassCard className="p-6 hover:scale-105 transition">

              <img
                src="/images/Branchimages/b_56.jpeg"
                alt="New St. Francis School Pithampur"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />

              <h3 className="text-2xl font-bold text-blue-900 mb-2">
                New St. Francis School – Pithampur
              </h3>

              <p className="text-slate-600 mb-4">
                MIG-II 225,226 Housing Board Colony Bagdoon Pithampur Sector-3 Dhar M.P.
              </p>

              <Link to="/branch/pithampur">
                <Button className="text-red-600">
                  Visit Pithampur Branch
                </Button>
              </Link>

            </GlassCard>
          </div>

        </div>
      </section>


      {/* Programs Section */}

      <section className="py-20 bg-white">

        <h2 className="text-4xl text-center font-bold text-blue-900 mb-12">
          Our Programs
        </h2>

        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 lg:grid-cols-5 gap-6">

          {programs.map((p, i) => (

            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
            >

              <GlassCard className="p-6 text-center hover:scale-105 transition">

                <h3 className="text-lg font-bold text-blue-900">
                  {p}
                </h3>

              </GlassCard>

            </motion.div>

          ))}

        </div>

      </section>

      {/* Testimonials */}

      <section className="py-20 bg-slate-50">

        <h2 className="text-4xl text-center font-bold text-blue-900 mb-12">
          What Parents Say
        </h2>

        <div className="max-w-4xl mx-auto space-y-6">

          {testimonials.map((t, i) => (

            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2 }}
              viewport={{ once: true }}
            >

              <GlassCard className="p-8 my-10 text-center hover:shadow-xl">

                <p className="text-lg text-slate-600 italic">
                  "{t.text}"
                </p>

                <h4 className="mt-4 font-bold text-blue-900">
                  {t.name}
                </h4>

              </GlassCard>

            </motion.div>

          ))}

        </div>

      </section>


      {/* Campus Tour */}

      {/* <section className="py-20 bg-white">

        <h2 className="text-4xl font-bold text-center text-blue-900 mb-12">
          Virtual Campus Tour
        </h2>

        <div className="max-w-5xl mx-auto px-4">

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >

            <div className="rounded-2xl overflow-hidden shadow-2xl">

              <iframe
                className="w-full h-[400px]"
                src="https://www.youtube.com/embed/tgbNymZ7vqY"
                title="Campus Tour"
                allowFullScreen
              />

            </div>

          </motion.div>

        </div>

      </section> */}



      {/* Floating Contact Buttons */}

      <div className="fixed bottom-6 right-6 flex flex-col gap-4 z-50">

        {/* Call Button */}
        <a
          href="tel:+919755402604"
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-blue-900 text-white shadow-xl hover:scale-110 transition-all duration-300"
        >
          <Phone className="w-6 h-6" />

          {/* Tooltip */}
          <span className="absolute right-16 opacity-0 group-hover:opacity-100 bg-blue-900 text-white text-xs px-3 py-1 rounded shadow transition">
            Call Us
          </span>
        </a>


        {/* WhatsApp Button */}
        <a
          href="https://wa.me/919755402604"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-green-500 text-white shadow-xl hover:scale-110 transition-all duration-300"
        >
          <MessageCircle className="w-6 h-6" />

          <span className="absolute right-16 opacity-0 group-hover:opacity-100 bg-green-500 text-white text-xs px-3 py-1 rounded shadow transition">
            WhatsApp
          </span>
        </a>


        {/* Admission Button */}
        <Link
          to="/admissions"
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-amber-500 text-white shadow-xl hover:scale-110 transition-all duration-300"
        >
          <GraduationCap className="w-6 h-6" />

          <span className="absolute right-16 opacity-0 group-hover:opacity-100 bg-amber-500 text-white text-xs px-3 py-1 rounded shadow transition">
            Admissions
          </span>
        </Link>

      </div>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-950 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Join Our School Community
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Give your child the education they deserve. Admissions open for the upcoming session.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg px-8 py-6">
                  Apply for Admission
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold text-lg px-8 py-6">
                  Schedule a Visit
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}